<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="fw-bold m-0">Przegląd dzisiejszych rezerwacji</h2>
      <small class="text-muted">
        Automatyczne odświeżanie za: <span class="fw-bold text-warning">{{ refreshTick }}s</span>
      </small>
    </div>

    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-warning" role="status">
        <span class="visually-hidden">Ładowanie...</span>
      </div>
      <p class="mt-3 text-muted">Ładowanie danych przeglądu...</p>
    </div>

    <div v-else>
      <!-- STATS -->
      <div class="row g-3 mb-4">
        <div class="col-sm-6 col-xl-3">
          <div class="card border-0 shadow-sm bg-body-tertiary h-100">
            <div class="card-body">
              <div class="text-muted small text-uppercase fw-bold mb-1">Nowe</div>
              <div class="fs-2 fw-bold text-info">{{ stats.newReservations }}</div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="card border-0 shadow-sm bg-body-tertiary h-100">
            <div class="card-body">
              <div class="text-muted small text-uppercase fw-bold mb-1">Dzisiejsze </div>
              <div class="fs-2 fw-bold text-success">{{ stats.todayReservations }}</div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="card border-0 shadow-sm bg-body-tertiary h-100">
            <div class="card-body">
              <div class="text-muted small text-uppercase fw-bold mb-1">Nieopłacone</div>
              <div class="fs-2 fw-bold text-danger">{{ stats.unpaidReservations }}</div>
            </div>
          </div>
        </div>
        <div class="col-sm-6 col-xl-3">
          <div class="card border-0 shadow-sm bg-body-tertiary h-100">
            <div class="card-body">
              <div class="text-muted small text-uppercase fw-bold mb-1">Anulowane</div>
              <div class="fs-2 fw-bold text-warning">{{ stats.cancelledReservations }}</div>
            </div>
          </div>
        </div>
      </div>

      <FiltersSection @filter-space="handleFilterSpace" />

      <ReservationsList
          :title="'Nadchodzące'"
          :icon="'angles-right'"
          :reservations="futureReservations"
          @confirm="handleConfirm"
          @unconfirm="handleUnconfirm"
          @paid="handlePaid"
          @unpaid="handleUnpaid"
          @cancel="handleCancellation"
          @restore="handleRestore"
      />
      <ReservationsList
          :title="'Przeszłe'"
          :icon="'angles-left'"
          :reservations="pastReservations"
          @confirm="handleConfirm"
          @unconfirm="handleUnconfirm"
          @paid="handlePaid"
          @unpaid="handleUnpaid"
          @cancel="handleCancellation"
          @restore="handleRestore"
      />
      <ReservationsList
          :title="'Anulowane'"
          :icon="'times'"
          :reservations="cancelledReservations"
          @confirm="handleConfirm"
          @unconfirm="handleUnconfirm"
          @paid="handlePaid"
          @unpaid="handleUnpaid"
          @cancel="handleCancellation"
          @restore="handleRestore"
      />
    </div>
  </div>

  <FullPageAlert
      v-if="alertConfig.show"
      :title="alertConfig.title"
      :message="alertConfig.message"
      :is-confirm="alertConfig.isConfirm"
      :confirm-text="alertConfig.confirmText"
      :cancel-text="alertConfig.cancelText"
      :duration="alertConfig.duration"
      @close="alertConfig.show = false"
      @confirm="onAlertConfirm"
      @cancel="alertConfig.show = false"
  />
</template>

<script>
import ReservationsList from './overview/ReservationsList.vue'
import {fetchWrapper} from '@/wrappers/api/fetchWrapper'
import FullPageAlert from "@/components/notifications/FullPageAlert.vue";
import FiltersSection from "@/components/admin/overview/FiltersSection.vue";

export default {
  name: 'AdminOverview',
  components: {
    FiltersSection,
    FullPageAlert,
    ReservationsList: ReservationsList
  },
  data() {
    return {
      isLoading: true,
      reservations: [],
      futureReservations: [],
      pastReservations: [],
      cancelledReservations: [],
      stats: {
        newReservations: 0,
        todayReservations: 0,
        unpaidReservations: 0,
        cancelledReservations: 0
      },
      alertConfig: {
        show: false,
        title: '',
        message: '',
        isConfirm: false,
        confirmText: 'Potwierdź',
        cancelText: 'Anuluj',
        duration: 0,
        onConfirmAction: null
      },
      refreshInterval: 30,
      refreshTick: 60,
      timer: null,
      isProcessing: false,
      filters: {
        spacesId: []
      }
    }
  },
  async mounted() {
    this.isLoading = true
    await this.fetchOverviewData()
    this.startAutoRefresh()
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  },
  methods: {
    startAutoRefresh() {
      if (this.timer) {
        clearInterval(this.timer)
      }
      this.refreshTick = this.refreshInterval
      this.timer = setInterval(() => {
        if (this.isProcessing || this.alertConfig.show) {
          return
        }
        if (this.refreshTick > 1) {
          this.refreshTick--
        } else {
          this.fetchOverviewData()
        }
      }, 1000)
    },
    isCancelled(reservation) {
      if (!reservation || !reservation.CanceledAt) return false
      return !reservation.CanceledAt.startsWith('0001-01-01')
    },
    async fetchOverviewData() {
      this.refreshTick = this.refreshInterval
      try {
        const response = await fetchWrapper.get('/admin/overview/1')
        if (response && response.overview_data) {
          const overview = response.overview_data

          if (overview.stats) {
            this.stats.newReservations = overview.stats.new_reservations ?? 0
            this.stats.todayReservations = overview.stats.today_reservations ?? 0
            this.stats.unpaidReservations = overview.stats.unpaid_reservations ?? 0
            this.stats.cancelledReservations = overview.stats.cancelled_reservations ?? 0
          }

          const rawReservations = Array.isArray(overview.today_reservations) ? overview.today_reservations : []

          const now = new Date()

          this.cancelledReservations = rawReservations.filter(res => this.isCancelled(res))
          this.reservations = rawReservations.filter(res => !this.isCancelled(res))

          if (this.filters.spacesId.length > 0) {
            this.reservations = this.reservations.filter(res => this.filters.spacesId.includes(res.SpaceId))
          }

          this.futureReservations = this.reservations.filter(res => new Date(res.StartDate) >= now)
          this.pastReservations = this.reservations.filter(res => new Date(res.StartDate) < now)
        }
      } catch (err) {
        console.warn('Nie udało się pobrać danych przeglądu admina:', err)
        this.reservations = []
        this.cancelledReservations = []
      } finally {
        this.isLoading = false
      }
    },
    showAlert({title, message, isConfirm = false, confirmText = 'Potwierdź', cancelText = 'Anuluj', duration = 0, onConfirm = null}) {
      this.alertConfig = {
        show: true,
        title,
        message,
        isConfirm,
        confirmText,
        cancelText,
        duration,
        onConfirmAction: onConfirm
      }
    },
    onAlertConfirm() {
      this.alertConfig.show = false
      if (typeof this.alertConfig.onConfirmAction === 'function') {
        this.alertConfig.onConfirmAction()
      }
    },
    handleFilterSpace(spacesId){
      this.filters.spacesId = spacesId
      this.fetchOverviewData()
    },

    /* Actions */
    async handleConfirm(reservation) {
      this.showAlert({
        title: 'Zatwierdzenie rezerwacji',
        message: `Czy na pewno chcesz zatwierdzić rezerwację "${reservation.TypeName}"?`,
        isConfirm: true,
        confirmText: 'Zatwierdź',
        onConfirm: () => this.executeConfirmation(reservation)
      })
    },
    async executeConfirmation(reservation) {
      this.isProcessing = true
      try {
        await fetchWrapper.post(`/admin/reservations/confirm`, {'uuid': reservation.Uuid})
        await this.fetchOverviewData()
        // Update local data
        reservation.ConfirmedAt = new Date().toISOString()
      } catch (err) {
        console.error('Błąd przy zatwierdzaniu rezerwacji:', err)
        this.showAlert({
          title: 'Błąd',
          message: 'Nie udało się zatwierdzić rezerwacji: ' + err
        })
      } finally {
        this.isProcessing = false
      }
    },
    async handleUnconfirm(reservation) {
      this.showAlert({
        title: 'Cofanie potwierdzenia rezerwacji',
        message: `Czy na pewno chcesz COFNĄĆ potwierdzenie rezerwacji "${reservation.TypeName}"?`,
        isConfirm: true,
        confirmText: 'Cofnij potwierdzenie',
        onConfirm: () => this.executeUnconfirm(reservation)
      })
    },
    async executeUnconfirm(reservation) {
      this.isProcessing = true
      try {
        await fetchWrapper.post(`/admin/reservations/unconfirm`, {'uuid': reservation.Uuid})
        await this.fetchOverviewData()
        // Update local data
        reservation.ConfirmedAt = null
      } catch (err) {
        console.error('Błąd przy cofaniu potwierdzenia rezerwacji:', err)
        this.showAlert({
          title: 'Błąd',
          message: 'Nie udało się cofnąć potwierdzenia rezerwacji: ' + err
        })
      } finally {
        this.isProcessing = false
      }
    },
    async handlePaid(reservation) {
      this.showAlert({
        title: 'Oznacz opłatę',
        message: `Czy chcesz oznaczyć jako opłaconą rezerwację "${reservation.TypeName}"?`,
        isConfirm: true,
        confirmText: 'Tak, oznacz jako opłacona',
        onConfirm: () => this.executePaid(reservation)
      })
    },
    async executePaid(reservation) {
      this.isProcessing = true
      try {
        await fetchWrapper.post(`/admin/reservations/paid`, {'uuid': reservation.Uuid})
        await this.fetchOverviewData()
        reservation.PaidAt = new Date().toISOString()
      } catch (err) {
        console.error('Błąd przy zatwierdzaniu opłaty:', err)
        this.showAlert({
          title: 'Błąd opłaty',
          message: 'Nie udało się zatwierdzić opłaty rezerwacji: ' + err
        })
      } finally {
        this.isProcessing = false
      }
    },
    async handleUnpaid(reservation) {
      this.showAlert({
        title: 'Oznacz jako niepłacona',
        message: `Czy chcesz oznaczyć rezerwację jako nieopłacona? "${reservation.TypeName}"?`,
        isConfirm: true,
        confirmText: 'Tak, oznacz jako nieopłacona',
        onConfirm: () => this.executeUnpaid(reservation)
      })
    },
    async executeUnpaid(reservation) {
      this.isProcessing = true
      try {
        await fetchWrapper.post(`/admin/reservations/unpaid`, {'uuid': reservation.Uuid})
        await this.fetchOverviewData()
        reservation.PaidAt = null
      } catch (err) {
        console.error('Błąd przy oznaczaniu rezeracji jako nieopłacona:', err)
        this.showAlert({
          title: 'Błąd opłaty',
          message: 'Nie udało się oznaczyć rezerwacji jako nieopłacona: ' + err
        })
      } finally {
        this.isProcessing = false
      }
    },
    async handleCancellation(reservation) {
      this.showAlert({
        title: 'Anuluj rezerwację',
        message: `Czy chcesz anulować rezerwację "${reservation.TypeName}"?`,
        isConfirm: true,
        confirmText: 'Anuluj rezerwację',
        cancelText: 'Zamknij',
        onConfirm: () => this.executeCancellation(reservation)
      })
    },
    async executeCancellation(reservation) {
      this.isProcessing = true
      try {
        await fetchWrapper.post(`/admin/reservations/cancel`, {'uuid': reservation.Uuid})
        await this.fetchOverviewData()
        // Update local data
        reservation.CanceledAt = new Date().toISOString()
      } catch (err) {
        console.error('Błąd przy anulowaniu rezerwacji:', err)
        this.showAlert({
          title: 'Błąd',
          message: 'Nie udało się anulować rezerwacji: ' + err
        })
      } finally {
        this.isProcessing = false
      }
    },
    async handleRestore(reservation) {
      this.showAlert({
        title: 'Przywróć rezerwację',
        message: `Czy chcesz przywrócić rezerwację "${reservation.TypeName}"?`,
        isConfirm: true,
        confirmText: 'Przywróć rezerwację',
        cancelText: 'Zamknij',
        onConfirm: () => this.executeRestore(reservation)
      })
    },
    async executeRestore(reservation) {
      this.isProcessing = true
      try {
        await fetchWrapper.post(`/admin/reservations/restore-cancelled`, {'uuid': reservation.Uuid})
        // Update local data
        reservation.CanceledAt = null
        await this.fetchOverviewData()
      } catch (err) {
        console.error('Błąd przy przywracaniu rezerwacji:', err)
        this.showAlert({
          title: 'Błąd',
          message: 'Nie udało się przywrócić rezerwacji: ' + err
        })
      } finally {
        this.isProcessing = false
      }
    }
  }
}
</script>